<template>
  <div class="d-flex flex-column row-gap-6" data-locator="manage-versions-content">
    <template v-if="isCurrentUserPm || currentUserRights.canUploadVersions">
      <DocumentSelection :selected-documents="selectedDocuments">
        <template #item>
          <DocumentSelectionListItem
            v-for="document in selectedDocuments"
            :key="document.id"
            :document="document"
          >
            <template #append>
              <template v-if="isWopiEnabled">
                <v-skeleton-loader v-if="checkWopiLockedFilesPending" type="text" width="100" class="ml-auto" />
                <v-chip
                  v-else-if="wopiLockedFiles.length > 0 && wopiLockedFiles.find(file => file.id === document.id)"
                  color="yellow-100"
                  class="font-weight-medium px-2 d-flex align-center ml-auto no-shrink"
                  label
                  size="small"
                >
                    <app-material-icon name="warning" color="inherit" filled />
                    <span class="ml-1">{{ $t(`${TRANSLATION_KEY_PREFIX}.editionOngoingTag`) }}</span>
                </v-chip>
              </template>
              <DocumentVersioningWopiDuplicationButton
                :document="document"
                :duplication-available="isWopiDuplicationAvailable"
                @duplicate="onDuplicate(document)"
              />
            </template>
          </DocumentSelectionListItem>
        </template>
      </DocumentSelection>
      <DocumentVersioningWopiDuplicationBox v-if="isWopiDuplicationBoxDisplayed" @hide="onHideBox" />
      <v-divider></v-divider>
      <div>
        <p class="text--large text--bold text-text-primary">{{ $t(`${TRANSLATION_KEY_PREFIX}.addVersionTitle`) }}</p>
        <template v-if="selectedDocuments.length > 0">
          <div class="d-flex align-center column-gap-2 text-text-secondary">
            <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.addVersionText`) }}</span>
            <div class="d-flex align-center column-gap-1">
              <app-badge>{{ `v${selectedDocuments[0].version}` }}</app-badge>
              <app-material-icon name="arrow_forward" />
              <app-badge variant="info">{{ `v${selectedDocuments[0].version + 1}` }}</app-badge>
            </div>
          </div>
        </template>
      </div>

      <template v-if="!newVersionFile">
        <app-segmented-control
          :active-option="newVersionFrom"
          :options="versioningSourcesOptions"
          @change-active-option="onChangeActiveOption"
        />

        <template v-if="newVersionFrom === VERSIONING_SOURCE_OPTIONS.FROM_CLOSD">
          <ClosdFildersSelector
            :validate-label="$t(`${TRANSLATION_KEY_PREFIX}.addVersionConfirmLabel`)"
            :disable-files-too-large-to-be-signed="false"
            :multiple="false"
            disable-folder-without-file
            files-only
            full-tree
            @select="onSelectedFiles"
          />
        </template>

        <template v-if="newVersionFrom === VERSIONING_SOURCE_OPTIONS.FROM_LOCAL">
          <v-file-input
            v-model="newVersionFile"
            color="info-darken-1"
            :class="{ clickable : !isAddVersionFormDisabled }"
            density="compact"
            :disabled="isAddVersionFormDisabled"
            hide-details
            variant="outlined"
            prepend-icon="attach_file"
            :label="$t(`${TRANSLATION_KEY_PREFIX}.newVersionFilePlaceholder`)"
          />
        </template>

        <template v-if="newVersionFrom === VERSIONING_SOURCE_OPTIONS.FROM_EXTERNAL">
          <ExternalFildersSelector select-only @select="onExternalFiles" />
        </template>
      </template>

      <template v-if="newVersionFile">
        <DocumentSelectionListItem
          class="text-text-secondary"
          :document="newVersionFile"
          removable
          variant="info"
          @remove="resetVersion"
        >
          <template #prepend>
            <app-badge variant="info">{{ `v${selectedDocuments[0].version + 1}` }}</app-badge>
          </template>
        </DocumentSelectionListItem>
      </template>

      <div class="d-flex flex-column row-gap-2">
        <v-checkbox
          v-model="cloneRightsFromPrevious"
          class="text-text-secondary"
          base-color="icon-secondary"
          hide-details
          :disabled="isAddVersionFormDisabled"
          :label="$t(`${TRANSLATION_KEY_PREFIX}.cloneRightsFromPrevious`)"
        />
        <v-checkbox
          v-model="sendNotification"
          class="text-text-secondary"
          base-color="icon-secondary"
          hide-details
          :disabled="isAddVersionFormDisabled"
          :label="$t(`${TRANSLATION_KEY_PREFIX}.sendNotificationLabel`)"
        />
        <template v-if="sendNotification">
          <VisualTextEditor
            v-model:value="notifyMessage"
            counter="footer"
            :error-messages="notifyMessageErrors"
            :max-length="notifyMessageMaxLength"
            :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.sendNotificationMessagePlaceholder`)"
          />
        </template>
      </div>
    </template>

    <v-row dense>
      <v-col>
        <app-button
          fullWidth
          type="outlined"
          variant="neutral"
          data-locator="cancel-button"
          @click="onClosePane"
        >
          {{ $t('common.cancel') }}
        </app-button>
      </v-col>
      <v-col>
        <app-button
          fullWidth
          :loading="isAddVersionLoading"
          :disabled="isAddVersionDisabled"
          data-locator="confirm-button"
          @click="onAddVersion"
        >
          {{ $t('common.add') }}
        </app-button>
      </v-col>
    </v-row>

    <DocumentOnGoingEditionDialog
      v-if="isDocumentOnGoingEditionDialogOpen"
      current-action="addVersion"
      :documents="selectedDocuments"
      @confirm="confirmAddVersion"
      @close="isDocumentOnGoingEditionDialogOpen = false"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import DocumentOnGoingEditionDialog from '@/common/dialogs/DocumentOnGoingEditionDialog.vue'
import DocumentSelection from '@/common/document-selection/DocumentSelection.vue'
import DocumentSelectionListItem from '@/common/document-selection/DocumentSelectionListItem.vue'
import DocumentVersioningWopiDuplicationBox from '@/common/document-versioning/DocumentVersioningWopiDuplicationBox.vue'
import DocumentVersioningWopiDuplicationButton from '@/common/document-versioning/DocumentVersioningWopiDuplicationButton.vue'
import { VERSIONING_EXTERNAL_PROVIDERS, VERSIONING_SOURCE_OPTIONS } from '@/common/document-versioning/types.ts'
import ClosdFildersSelector from '@/common/filders/ClosdFildersSelector.vue'
import ExternalFildersSelector from '@/common/filders/ExternalFildersSelector.vue'
import { getFileExtension, isFileLowerThanMaxSignableSize } from '@/common/utils/files'
import {
  getOfficeDocumentType,
  OFFICE_EDITION_MAX_FILE_SIZE,
} from '@/common/utils/office'
import { escapeHtml } from '@/common/utils/strings'
import { getUserSetting } from '@/common/utils/userSettings'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { CHECK_WOPI_LOCKED_FILES } from '@/store/modules/documents/action_types'
import { RESET_WOPI_LOCKED_FILES } from '@/store/modules/documents/mutation_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 400
const WOPI_DUPLICATION_BOX_USER_SETTINGS_KEY = 'hide-wopi-duplication-box'
const TRANSLATION_KEY_PREFIX = 'common.document-versioning.DocumentVersioning'

export default defineComponent({
  name: 'DocumentVersioning',
  components: {
    ClosdFildersSelector,
    DocumentOnGoingEditionDialog,
    DocumentSelection,
    DocumentSelectionListItem,
    DocumentVersioningWopiDuplicationBox,
    DocumentVersioningWopiDuplicationButton,
    ExternalFildersSelector,
    VisualTextEditor,
  },
  emits: ['add-version', 'add-version-from-imanage', 'close-pane', 'setIsDocumentOnGoingEditionDialogOpen'],
  props: {
    loading: {
      type: Boolean,
    },
    selectedDocuments: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      VERSIONING_SOURCE_OPTIONS,
      cloneRightsFromPrevious: true,
      hideWopiDuplicationBox: false,
      isDocumentOnGoingEditionDialogOpen: false,
      isImanageFilesPickerDialogOpen: false,
      newVersionFile: null,
      newVersionFrom: VERSIONING_SOURCE_OPTIONS.FROM_CLOSD,
      newVersionFromProvider: null,
      notifyMessage: '',
      notifyMessageMaxLength: NOTIFY_MESSAGE_MAX_LENGTH,
      sendNotification: false,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm', 'currentUserRights', 'isWopiEnabled', 'roomMnemo', 'isDataRoom']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapState('documents', ['checkWopiLockedFilesPending', 'wopiLockedFiles']),
    ...mapGetters('user', ['currentUserId', 'polyOfficeEnabled']),
    hasExternalServiceEnabled () {
      return this.imanageEnabledAndLogged || this.polyOfficeEnabled
    },
    isAddVersionDisabled () {
      return !this.newVersionFile || this.isNotifyMessageTooLong || this.isDocumentOnGoingEditionDialogOpen
    },
    isAddVersionFormDisabled () {
      return this.isDocumentOnGoingEditionDialogOpen
    },
    isAddVersionLoading () {
      return this.checkWopiLockedFilesPending || this.loading
    },
    isVersioningFromImanage () {
      return this.newVersionFrom === VERSIONING_SOURCE_OPTIONS.FROM_EXTERNAL && this.newVersionFromProvider === VERSIONING_EXTERNAL_PROVIDERS.IMANAGE
    },
    isNotifyMessageTooLong () {
      return this.notifyMessageLength > this.notifyMessageMaxLength
    },
    isWopiDuplicationAvailable () {
      return this.newVersionFile === null && !this.isDataRoom
    },
    isWopiDuplicationBoxDisplayed () {
      const shouldShowBox = this.selectedDocuments.filter(doc => {
        return doc.isUserFromSub && this.isDocumentSizeUnderOfficeEditionLimit(doc)
      })
      return this.shouldCheckForWopiLockOnFiles && !getUserSetting(this.currentUserId, WOPI_DUPLICATION_BOX_USER_SETTINGS_KEY) && this.isWopiDuplicationAvailable && shouldShowBox.length
    },
    notifyMessageErrors () {
      const errors = []

      if (this.isNotifyMessageTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    notifyMessageLength () {
      return escapeHtml(this.notifyMessage).length
    },
    possiblyWopiLockedFiles () {
      return this.selectedDocuments.filter(document => document.type === 'file' && document.canEdit)
    },
    shouldCheckForWopiLockOnFiles () {
      return this.isWopiEnabled && this.possiblyWopiLockedFiles.length > 0
    },
    versioningSourcesOptions () {
      const versioningSources = Object.values(VERSIONING_SOURCE_OPTIONS).filter(option => option !== VERSIONING_SOURCE_OPTIONS.FROM_EXTERNAL || this.hasExternalServiceEnabled)
      return versioningSources.map(option => {
        return {
          name: this.$t(`${TRANSLATION_KEY_PREFIX}.versioningSourcesOptionsLabels.${option}`),
          value: option,
        }
      })
    },
  },
  beforeUnmount () {
    this.RESET_WOPI_LOCKED_FILES()
  },
  mounted () {
    if (this.shouldCheckForWopiLockOnFiles) { this.checkWopiLockedFiles() }
  },
  watch: {
    isDocumentOnGoingEditionDialogOpen (newVal) {
      this.$emit('setIsDocumentOnGoingEditionDialogOpen', newVal)
    },
  },
  methods: {
    ...mapActions('documents', [CHECK_WOPI_LOCKED_FILES]),
    ...mapMutations('documents', [RESET_WOPI_LOCKED_FILES]),
    async checkWopiLockedFiles () {
      try {
        return await this.CHECK_WOPI_LOCKED_FILES({
            mnemo: this.roomMnemo,
            filesIds: this.possiblyWopiLockedFiles.map(file => file.id),
        })
      } catch (e) {
        console.error(e)
      }
    },
    async onAddVersion () {
      if (!this.shouldCheckForWopiLockOnFiles) {
        this.confirmAddVersion()
      } else {
        const response = await this.checkWopiLockedFiles()

        if (response.lockedFiles.length > 0) {
          this.isDocumentOnGoingEditionDialogOpen = true
        } else {
          this.confirmAddVersion()
        }
      }
    },
    onChangeActiveOption (option) {
      this.newVersionFrom = option
    },
    onClosePane () {
      this.$emit('close-pane')
    },
    onDuplicate (document) {
      this.newVersionFrom = VERSIONING_SOURCE_OPTIONS.FROM_CLOSD
      this.newVersionFile = document
    },
    onHideBox (value) {
      this.hideWopiDuplicationBox = value
    },
    onOpenIManageFilesPicker () {
      this.isImanageFilesPickerDialogOpen = true
    },
    onExternalFiles (data) {
      this.newVersionFromProvider = data.provider

      const file = data.files.output_data?.selected[0] || data.files[0]

      if (file) {
        this.newVersionFile = {
          id: file.id,
          name: file.name,
          extension: file.extension,
        }
      } else {
        this.newVersionFile = files.selected
      }
    },
    onSelectedFiles (document) {
      this.newVersionFile = document[0]
    },
    prepareAddVersion () {
      const formData = new FormData()

      if (this.newVersionFrom === VERSIONING_SOURCE_OPTIONS.FROM_LOCAL) {
        formData.append('fromClosd', false)
        formData.append('fileSelect', this.newVersionFile)
      } else {
        formData.append('fromClosd', true)
        formData.append('fileSelect', this.newVersionFile.id)
      }

      formData.append('formCloneRightsFromPrevious', this.cloneRightsFromPrevious)
      formData.append('formToggleNotif', this.sendNotification)
      formData.append('formNotifMessage', this.notifyMessage)

      this.$emit('add-version', formData)
    },
    prepareAddVersionFromIManage (targetId) {
      const formData = {
        fileId: this.selectedDocuments[0].id,
        targetId: targetId,
        formCloneRightsFromPrevious: this.cloneRightsFromPrevious,
        formToggleNotif: this.sendNotification,
        formNotifMessage: this.notifyMessage,
      }

      this.$emit('add-version-from-imanage', formData)
    },
    resetVersion () {
      this.newVersionFile = null
      this.newVersionFromProvider = null
    },
    confirmAddVersion () {
      if (this.hideWopiDuplicationBox) {
        setUserSetting(this.currentUserId, WOPI_DUPLICATION_BOX_USER_SETTINGS_KEY, this.hideWopiDuplicationBox)
      }

      if (this.isVersioningFromImanage) {
        this.prepareAddVersionFromIManage(this.newVersionFile.id || this.newVersionFile)
      } else {
        this.prepareAddVersion()
      }
      // Reset here because the dialog doesn't have time to close
      // to get the close event when click confirm
      this.isDocumentOnGoingEditionDialogOpen = false
    },
    isDocumentSizeUnderOfficeEditionLimit (doc) {
      if (!this.officeDocumentType) {
        return true
      }

      const editionMaxFileSize = OFFICE_EDITION_MAX_FILE_SIZE[this.officeDocumentType(doc)]
      return isFileLowerThanMaxSignableSize(doc, editionMaxFileSize)
    },
    officeDocumentType (doc) {
      const fileExtension = getFileExtension(doc)

      if (!fileExtension) {
        return null
      }

      return getOfficeDocumentType(fileExtension)
    },
  },
})
</script>

<style scoped lang="scss">
  :deep(.v-tab) {
    font-size: 14px !important;
  }

  :deep(.v-file-input) {
    min-height: 48px;

    .v-label {
      font-size: 14px;
    }
  }

  .v-application .v-text-field.v-input--disabled {
    background-color: transparent !important;
  }

  :deep(.v-input--disabled .v-input__slot) {
    cursor: default !important;
  }

  .v-input--selection-controls {
    margin-top: 0;
  }
</style>
